import type {
  CreateFolderRequest,
  CreateUploadFileRequest,
  CreateUrlFileRequest,
  FileDetailResponse,
  FileDownloadResponse,
  FileResponse,
  UpdateFileRequest,
} from '@doneverse/shared'
import type { NewVersionInput } from '@doneverse/shared/validation'
import { api } from '../api/index.js'
import { uploadToSignedUrl } from './uploadService.js'

/** In-flight `findOrCreateFolder` calls, keyed by parent + name. */
const folderLookups = new Map<string, Promise<FileResponse>>()

/**
 * Everything `GET /v1/files` narrows a listing by, plus the paging controls.
 *
 * Omitting `parentId` asks for the root level, matching the endpoint — it is
 * not "every file in the workspace".
 */
export interface ListFilesQuery {
  parentId?: string
  kind?: FileResponse['kind']
  /** Case-insensitive substring match on the name, applied in the database. */
  search?: string
  /** 1-based. Only meaningful alongside `limit`. */
  page?: number
  limit?: number
}

/** One page of files, plus the size of the whole filtered set behind it. */
export interface FilePage {
  data: FileResponse[]
  /** Rows matching the filters, NOT rows on this page. */
  total: number
}

function toFileParams(query: ListFilesQuery): Record<string, string> {
  const params: Record<string, string> = {}
  if (query.parentId) params.parent_id = query.parentId
  if (query.kind) params.kind = query.kind
  if (query.search) params.search = query.search
  if (query.page) params.page = String(query.page)
  if (query.limit) params.limit = String(query.limit)
  return params
}

/**
 * List files, optionally one page at a time.
 *
 * **`limit` is the switch**, exactly as on the endpoint: omit it and you get
 * every matching row as a plain array, pass it and you get `{ data, total }`
 * where `total` counts the whole filtered set rather than the page — so a
 * caller can say "50 of 157" instead of guessing from what it holds.
 *
 * There is deliberately no default page size here either. A default would
 * silently truncate whichever caller grows past it next, which is the exact bug
 * opt-in pagination exists to avoid.
 *
 * Declared out here rather than inline because an object literal cannot carry
 * overload signatures.
 */
function listFiles(query?: Omit<ListFilesQuery, 'limit'>): Promise<FileResponse[]>
function listFiles(query: ListFilesQuery & { limit: number }): Promise<FilePage>
async function listFiles(query: ListFilesQuery = {}): Promise<FileResponse[] | FilePage> {
  const body = await api
    .query(toFileParams(query))
    .get('/v1/files')
    .json<{ data: FileResponse[]; total?: number }>()

  if (query.limit === undefined) return body.data
  return { data: body.data, total: body.total ?? body.data.length }
}

export const fileService = {
  listFiles,

  getFile: async (id: string): Promise<FileDetailResponse> => {
    return api.get(`/v1/files/${id}`).json<FileDetailResponse>()
  },

  createUploadFile: async (
    file: File,
    name: string,
    parentId?: string,
    onProgress?: (percent: number) => void,
  ): Promise<FileResponse> => {
    const contentType = file.type || 'application/octet-stream'
    const body: CreateUploadFileRequest = {
      kind: 'upload',
      name,
      content_type: contentType,
      ...(parentId && { parent_id: parentId }),
    }

    const result = await api.post(body, '/v1/files').json<FileResponse & { uploadUrl: string }>()

    await uploadToSignedUrl(file, result.uploadUrl, onProgress, contentType)

    return result
  },

  requestUpload: async (
    name: string,
    contentType: string,
    parentId?: string | null,
    description?: string,
  ): Promise<FileResponse & { uploadUrl: string }> => {
    const body: CreateUploadFileRequest = {
      kind: 'upload',
      name,
      content_type: contentType || 'application/octet-stream',
      ...(parentId && { parent_id: parentId }),
      ...(description && { description }),
    }
    return api.post(body, '/v1/files').json<FileResponse & { uploadUrl: string }>()
  },

  /**
   * Add a link as a `url` file. It lives in the files tree like any other row —
   * it can be renamed, described, moved into folders, and deleted — but has no
   * stored bytes and therefore no versions.
   */
  createUrlFile: async (input: {
    name: string
    url: string
    parentId?: string | null
    description?: string
  }): Promise<FileResponse> => {
    const body: CreateUrlFileRequest = {
      kind: 'url',
      name: input.name,
      url: input.url,
      ...(input.parentId && { parent_id: input.parentId }),
      ...(input.description && { description: input.description }),
    }
    return api.post(body, '/v1/files').json<FileResponse>()
  },

  createFolder: async (name: string, parentId?: string): Promise<FileResponse> => {
    const body: CreateFolderRequest = {
      kind: 'folder',
      name,
      ...(parentId && { parent_id: parentId }),
    }
    return api.post(body, '/v1/files').json<FileResponse>()
  },

  /**
   * The folder called `name` under `parentId`, creating it if it is not there.
   *
   * `POST /v1/files` does not dedupe folder names, so a feature that wants ONE
   * folder has to look before it creates — and has to stop concurrent callers
   * racing each other into two. The in-flight promise below is keyed by
   * parent+name so two different folders resolving at once cannot collide on
   * one slot.
   *
   * Note this does not cache the RESULT: the listing is per workspace (the
   * active-workspace header decides it), so a remembered id would be wrong
   * after a workspace switch. Callers that attach repeatedly should hold the
   * resolved id themselves, keyed by workspace.
   *
   * The lookup is a **name search, not a page**. Paging it would be the actual
   * hazard: a folder that exists on page 2 of a wide parent reads as absent, and
   * "absent" here means CREATE — so a limit would hand out duplicate folders,
   * exactly the outcome the in-flight slot below exists to prevent. `search` and
   * `kind` push the matching into the database, so this is one bounded read no
   * matter how many siblings the parent has.
   */
  findOrCreateFolder: async (name: string, parentId?: string): Promise<FileResponse> => {
    const slot = `${parentId ?? 'root'}/${name}`
    const existing = folderLookups.get(slot)
    if (existing) return existing

    const lookup = (async () => {
      const candidates = await fileService.listFiles({ parentId, kind: 'folder', search: name })
      // `search` is a case-insensitive SUBSTRING match, so it narrows rather
      // than decides; the exact name (and kind) is still settled here.
      const match = candidates.find((entry) => entry.kind === 'folder' && entry.name === name)
      return match ?? (await fileService.createFolder(name, parentId))
    })().finally(() => folderLookups.delete(slot))

    folderLookups.set(slot, lookup)
    return lookup
  },

  updateFile: async (id: string, body: UpdateFileRequest): Promise<FileResponse> => {
    return api.patch(body, `/v1/files/${id}`).json<FileResponse>()
  },

  requestNewVersion: async (
    id: string,
    contentType: string,
    comment: string,
  ): Promise<FileResponse & { uploadUrl: string }> => {
    const body: NewVersionInput = {
      content_type: contentType || 'application/octet-stream',
      comment,
    }
    return api.post(body, `/v1/files/${id}`).json<FileResponse & { uploadUrl: string }>()
  },

  /**
   * A signed URL for the file's current version carrying an `attachment`
   * disposition, so following it saves the file rather than previewing it.
   * Expires within the hour — fetch it on click, never cache it.
   */
  getDownloadUrl: async (id: string): Promise<FileDownloadResponse> => {
    return api.get(`/v1/files/${id}/download`).json<FileDownloadResponse>()
  },

  deleteFile: async (id: string): Promise<void> => {
    await api.delete(`/v1/files/${id}`).res()
  },
}
